import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useProject, useUpdateProject, useDeleteProject, useStages } from '../api/projects'
import { useDocumentsByProject, useDeleteDocument, getDocumentDownloadUrl } from '../api/documents'
import { DocumentUploadModal } from '../components/documents/DocumentUploadModal'
import { useActivities } from '../api/activities'
import { PaymentsSection } from '../components/projects/PaymentsSection'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { cn, formatDate } from '../lib/utils'

type Tab = 'overview' | 'payments' | 'documents' | 'history'

const priorityLabels = {
  low: 'Низкий',
  medium: 'Средний',
  high: 'Высокий',
  urgent: 'Срочный',
}

const priorityColors = {
  low: 'bg-text-tertiary',
  medium: 'bg-accent-blue',
  high: 'bg-accent-orange',
  urgent: 'bg-red-500',
}

function formatBudget(budget: string | undefined | null, currency: string = 'RUB'): string {
  if (!budget) return '—'
  const num = parseFloat(budget)
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num)
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [isEditing, setIsEditing] = useState(false)

  const { data: project, isLoading, error } = useProject(id!)
  const { data: stages } = useStages()
  const updateProject = useUpdateProject()
  const deleteProject = useDeleteProject()

  const [formData, setFormData] = useState<{
    name: string
    description: string
    stageId: string
    priority: 'low' | 'medium' | 'high' | 'urgent'
    budget: string
    startDate: string
    deadline: string
  }>({
    name: '',
    description: '',
    stageId: '',
    priority: 'medium',
    budget: '',
    startDate: '',
    deadline: '',
  })

  // Initialize form when project loads
  const initForm = () => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        stageId: project.stageId || '',
        priority: project.priority,
        budget: project.budget || '',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        deadline: project.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '',
      })
    }
  }

  const handleEdit = () => {
    initForm()
    setIsEditing(true)
  }

  const handleSave = async () => {
    await updateProject.mutateAsync({
      id: id!,
      data: {
        name: formData.name,
        description: formData.description || undefined,
        stageId: formData.stageId || null,
        priority: formData.priority,
        budget: formData.budget || null,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
      },
    })
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (confirm('Удалить проект? Это действие нельзя отменить.')) {
      await deleteProject.mutateAsync(id!)
      navigate('/projects')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Проект не найден</div>
      </div>
    )
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'payments', label: 'Платежи' },
    { id: 'documents', label: 'Документы' },
    { id: 'history', label: 'История' },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-5 border-b border-border-subtle bg-bg-secondary">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/projects"
            className="w-8 h-8 rounded-md bg-bg-tertiary flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>

          <div className="flex-1">
            {isEditing ? (
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="text-xl font-bold"
              />
            ) : (
              <h1 className="text-2xl font-bold tracking-tight">{project.name}</h1>
            )}
          </div>

          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(false)}>
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={updateProject.isPending}
                >
                  {updateProject.isPending ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={handleEdit}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Редактировать
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Удалить
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-accent-lime/15 text-accent-lime'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="col-span-2 space-y-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Информация о проекте</h3>

                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">
                          Описание
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                          className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
                          rows={4}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-1.5">
                            Стадия
                          </label>
                          <select
                            value={formData.stageId}
                            onChange={(e) => setFormData((p) => ({ ...p, stageId: e.target.value }))}
                            className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                          >
                            <option value="">Не назначена</option>
                            {stages?.map((stage) => (
                              <option key={stage.id} value={stage.id}>
                                {stage.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-1.5">
                            Приоритет
                          </label>
                          <select
                            value={formData.priority}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent',
                              }))
                            }
                            className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary focus:outline-none focus:border-accent-lime/50"
                          >
                            <option value="low">Низкий</option>
                            <option value="medium">Средний</option>
                            <option value="high">Высокий</option>
                            <option value="urgent">Срочный</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <Input
                          label="Бюджет (₽)"
                          type="number"
                          value={formData.budget}
                          onChange={(e) => setFormData((p) => ({ ...p, budget: e.target.value }))}
                        />
                        <Input
                          label="Дата начала"
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))}
                        />
                        <Input
                          label="Дедлайн"
                          type="date"
                          value={formData.deadline}
                          onChange={(e) => setFormData((p) => ({ ...p, deadline: e.target.value }))}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {project.description && (
                        <p className="text-text-secondary">{project.description}</p>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-text-tertiary">Стадия</span>
                          <p className="font-medium flex items-center gap-2">
                            {project.stage && (
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: project.stage.color }}
                              />
                            )}
                            {project.stage?.name || '—'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-text-tertiary">Приоритет</span>
                          <p className="font-medium flex items-center gap-2">
                            <span className={cn('w-2 h-2 rounded-full', priorityColors[project.priority])} />
                            {priorityLabels[project.priority]}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-text-tertiary">Бюджет</span>
                          <p className="font-mono font-semibold text-accent-lime">
                            {formatBudget(project.budget, project.currency)}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-text-tertiary">Дедлайн</span>
                          <p className="font-medium">
                            {project.deadline ? formatDate(project.deadline) : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Client */}
              {project.client && (
                <Card>
                  <a href="/clients" className="block p-6 hover:bg-white/5 transition-colors rounded-xl">
                    <h3 className="text-lg font-semibold mb-4">Клиент</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-bg-tertiary flex items-center justify-center text-sm font-semibold">
                        {(project.client.companyName || project.client.contactName)
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-accent-blue">
                          {project.client.companyName || project.client.contactName}
                        </p>
                        {project.client.email && (
                          <p className="text-sm text-text-tertiary">{project.client.email}</p>
                        )}
                      </div>
                    </div>
                  </a>
                </Card>
              )}

              {/* Dates */}
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Даты</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Создан</span>
                      <span>{formatDate(project.createdAt)}</span>
                    </div>
                    {project.startDate && (
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Начало</span>
                        <span>{formatDate(project.startDate)}</span>
                      </div>
                    )}
                    {project.deadline && (
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Дедлайн</span>
                        <span className="text-accent-orange">{formatDate(project.deadline)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-text-tertiary">Обновлён</span>
                      <span>{formatDate(project.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="max-w-2xl">
            <PaymentsSection
              projectId={project.id}
              payments={project.payments || []}
            />
          </div>
        )}

        {activeTab === 'documents' && (
          <ProjectDocumentsTab projectId={project.id} />
        )}

        {activeTab === 'history' && (
          <ProjectHistoryTab projectId={project.id} />
        )}
      </div>
    </div>
  )
}

function ProjectDocumentsTab({ projectId }: { projectId: string }) {
  const { data: documents, isLoading } = useDocumentsByProject(projectId)
  const deleteDocument = useDeleteDocument()
  const [showUploadModal, setShowUploadModal] = useState(false)

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Удалить документ "${name}"?`)) {
      await deleteDocument.mutateAsync(id)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-text-tertiary">Загрузка...</div>
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Документы проекта</h3>
        <Button
          variant="primary"
          onClick={() => setShowUploadModal(true)}
        >
          Загрузить
        </Button>
      </div>

      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        projectId={projectId}
      />

      {documents?.length ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{doc.name}</p>
                <p className="text-xs text-text-tertiary">
                  {doc.type} · {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '—'} · {formatDate(doc.createdAt)}
                </p>
              </div>
              <a
                href={getDocumentDownloadUrl(doc.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm text-text-secondary hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                Скачать
              </a>
              <button
                onClick={() => handleDelete(doc.id, doc.name)}
                className="p-1.5 text-text-tertiary hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-text-tertiary">
          <p>Нет документов</p>
          <p className="text-sm mt-1">Загрузите документ для этого проекта</p>
        </div>
      )}
    </div>
  )
}

function ProjectHistoryTab({ projectId }: { projectId: string }) {
  const { data: activities, isLoading } = useActivities({
    entityType: 'project',
    entityId: projectId,
    limit: 50,
  })

  if (isLoading) {
    return <div className="text-center py-12 text-text-tertiary">Загрузка...</div>
  }

  if (!activities?.length) {
    return <div className="text-center py-12 text-text-tertiary">Нет истории изменений</div>
  }

  return (
    <div className="max-w-2xl">
      <h3 className="text-lg font-semibold mb-4">История изменений</h3>
      <div className="space-y-4">
        {activities.map((activity, i) => (
          <div key={activity.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="w-2.5 h-2.5 rounded-full bg-accent-lime flex-shrink-0" />
              {i < activities.length - 1 && <div className="w-px flex-1 bg-white/10 mt-1" />}
            </div>
            <div className="flex-1 pb-4">
              <p className="text-sm text-text-primary">{activity.content}</p>
              <div className="flex items-center gap-2 mt-1">
                {activity.user && (
                  <span className="text-xs text-text-tertiary">{activity.user.username}</span>
                )}
                <span className="text-xs text-text-tertiary">{formatDate(activity.createdAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
